import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  Cpu, Wifi, Router, Thermometer, Radio
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';
import SelectWithCreate from '../../components/ui/select-with-create';

const IoT = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'iot');
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({ total: 0, por_tipo: [], por_empresa: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'asset_tag', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);

  // Form options
  const [options, setOptions] = useState({ empresas: [], sites: [], brands: [], tipos: [], estados: [] });
  
  // Filter options (only values that exist in iot table)
  const [filterOptions, setFilterOptions] = useState({ empresas: [], sites: [], brands: [], tipos: [], estados: [] });

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, device: null });
  const [editModal, setEditModal] = useState({ open: false, device: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, device: null });
  const [formData, setFormData] = useState({
    empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '',
    asset_tag: '', serial_number: '', modelo: '', ip_address: '', mac_address: '', ubicacion: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/iot/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/assets/iot/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.tipo_id) params.append('tipo_id', filters.tipo_id);
      if (filters.status_id) params.append('status_id', filters.status_id);

      const response = await api.get(`/assets/iot?${params}`);
      setDevices(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar dispositivos IoT');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [page, pageSize, filters.search, filters.empresa_id, filters.site_id, filters.brand_id, filters.tipo_id, filters.status_id, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);
  
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/iot/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Stat card click handler
  const handleStatFilter = (filterType, tipoId = null) => {
    setActiveStatFilter(filterType);
    setPage(1);
    setSearchInput('');
    if (filterType === 'all') {
      setFilters({ search: '', empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '' });
    } else if (tipoId) {
      setFilters(prev => ({ ...prev, tipo_id: tipoId.toString(), search: '', empresa_id: '', site_id: '', brand_id: '', status_id: '' }));
    }
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Get icon for tipo
  const getTipoIcon = (tipoNombre) => {
    const iconMap = {
      'Sensor': Thermometer,
      'Gateway': Router,
      'Beacon': Radio,
      'Tracker': Wifi,
    };
    return iconMap[tipoNombre] || Cpu;
  };

  // View modal
  const handleView = (device) => {
    setViewModal({ open: true, device });
  };

  // Edit modal
  const handleEdit = (device) => {
    setFormData({
      empresa_id: device.empresa_id?.toString() || '',
      site_id: device.site_id?.toString() || '',
      brand_id: device.brand_id?.toString() || '',
      tipo_id: device.tipo_id?.toString() || '',
      status_id: device.status_id?.toString() || '',
      asset_tag: device.asset_tag || '',
      serial_number: device.serial_number || '',
      modelo: device.modelo || '',
      ip_address: device.ip_address || '',
      mac_address: device.mac_address || '',
      ubicacion: device.ubicacion || ''
    });
    setEditModal({ open: true, device, isCopy: false });
  };

  // Copy modal
  const handleCopy = (device) => {
    setFormData({
      empresa_id: device.empresa_id?.toString() || '',
      site_id: device.site_id?.toString() || '',
      brand_id: device.brand_id?.toString() || '',
      tipo_id: device.tipo_id?.toString() || '',
      status_id: device.status_id?.toString() || '',
      asset_tag: '', // Unique - clear
      serial_number: '', // Unique - clear
      modelo: device.modelo || '',
      ip_address: '', // Unique - clear
      mac_address: '', // Unique - clear
      ubicacion: device.ubicacion || ''
    });
    setEditModal({ open: true, device: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '',
      asset_tag: '', serial_number: '', modelo: '', ip_address: '', mac_address: '', ubicacion: ''
    });
    setEditModal({ open: true, device: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.asset_tag.trim()) {
      toast.error('El Asset Tag es requerido');
      return;
    }
    if (!formData.empresa_id) {
      toast.error('La Empresa es requerida');
      return;
    }
    if (!formData.site_id) {
      toast.error('El Site es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        empresa_id: parseInt(formData.empresa_id),
        site_id: parseInt(formData.site_id),
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        tipo_id: formData.tipo_id ? parseInt(formData.tipo_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        asset_tag: formData.asset_tag.trim(),
        serial_number: formData.serial_number || null,
        modelo: formData.modelo || null,
        ip_address: formData.ip_address || null,
        mac_address: formData.mac_address || null,
        ubicacion: formData.ubicacion || null
      };

      if (editModal.device && !editModal.isCopy) {
        await api.put(`/assets/iot/${editModal.device.id}`, payload);
        toast.success('Dispositivo actualizado exitosamente');
      } else {
        await api.post('/assets/iot', payload);
        toast.success('Dispositivo creado exitosamente');
      }
      setEditModal({ open: false, device: null, isCopy: false });
      fetchDevices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/iot/${deleteModal.device.id}`);
      toast.success('Dispositivo eliminado exitosamente');
      setDeleteModal({ open: false, device: null });
      fetchDevices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Quick create handlers
  const handleQuickCreateBrand = async (nombre) => {
    const response = await api.post('/assets/iot/quick-create/brand', { nombre });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateTipo = async (nombre) => {
    const response = await api.post('/assets/iot/quick-create/tipo', { nombre });
    await fetchOptions();
    return response.data;
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver IoT</p>
        </div>
      </Layout>
    );
  }

  // Get top 3 tipos for stats cards
  const topTipos = stats.por_tipo.filter(t => t.count > 0).slice(0, 3);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">IoT</h1>
            <p className="page-subtitle">Gestion de dispositivos IoT</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/iot"
              filters={{ search: filters.search }}
              filename="iot_devices"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-iot-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Dispositivo
              </Button>
            )}
          </div>
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="iot-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Cpu className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          {topTipos.map((tipo, idx) => {
            const TipoIcon = getTipoIcon(tipo.nombre);
            const colors = ['text-green-500', 'text-blue-500', 'text-amber-500'];
            const rings = ['ring-green-500', 'ring-blue-500', 'ring-amber-500'];
            return (
              <div 
                key={tipo.id}
                className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:${rings[idx]}/50 ${activeStatFilter === `tipo_${tipo.id}` ? `ring-2 ${rings[idx]}` : ''}`}
                onClick={() => handleStatFilter(`tipo_${tipo.id}`, tipo.id)}
                data-testid={`stats-tipo-${tipo.id}`}
              >
                <TipoIcon className={`h-6 w-6 ${colors[idx]} flex-shrink-0`} strokeWidth={1.5} />
                <div>
                  <div className={`stats-card-value ${colors[idx]}`}>{tipo.count}</div>
                  <div className="stats-card-label">{tipo.nombre}</div>
                </div>
              </div>
            );
          })}
          {/* Fill empty slots if less than 3 tipos */}
          {[...Array(3 - topTipos.length)].map((_, idx) => (
            <div key={`empty-${idx}`} className="stats-card flex items-center gap-3 opacity-50">
              <Cpu className="h-6 w-6 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              <div>
                <div className="stats-card-value text-muted-foreground">0</div>
                <div className="stats-card-label">-</div>
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('asset_tag')}
                        >
                          <div className="flex items-center gap-1">
                            ASSET TAG
                            <SortIcon field="asset_tag" />
                          </div>
                        </th>
                        <th>TIPO</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('modelo')}
                        >
                          <div className="flex items-center gap-1">
                            MODELO
                            <SortIcon field="modelo" />
                          </div>
                        </th>
                        <th>MARCA</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_address')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip_address" />
                          </div>
                        </th>
                        <th>EMPRESA</th>
                        <th>SITE</th>
                        <th>ESTADO</th>
                        <th className="w-32 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.tipo_id || ""}
                            onChange={(e) => handleFilterChange('tipo_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.tipos?.map(t => (
                              <option key={t.id} value={t.id}>{t.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.brand_id || ""}
                            onChange={(e) => handleFilterChange('brand_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todas</option>
                            {filterOptions.brands?.map(b => (
                              <option key={b.id} value={b.id}>{b.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.empresa_id || ""}
                            onChange={(e) => handleFilterChange('empresa_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todas</option>
                            {filterOptions.empresas?.map(e => (
                              <option key={e.id} value={e.id}>{e.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.site_id || ""}
                            onChange={(e) => handleFilterChange('site_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.sites?.map(s => (
                              <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.status_id || ""}
                            onChange={(e) => handleFilterChange('status_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.estados?.map(e => (
                              <option key={e.id} value={e.id}>{e.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.length === 0 ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="empty-state">
                              <Cpu className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron dispositivos IoT</p>
                            </div>
                          </td>
                        </tr>
                      ) : devices.map((device) => (
                        <tr key={device.id} data-testid={`iot-row-${device.id}`}>
                          <td className="font-medium text-primary">{device.asset_tag}</td>
                          <td>
                            {device.tipo_nombre ? (
                              <span className="badge badge-outline">{device.tipo_nombre}</span>
                            ) : '-'}
                          </td>
                          <td className="text-muted-foreground">{device.modelo || '-'}</td>
                          <td className="text-muted-foreground">{device.brand_nombre || '-'}</td>
                          <td className="font-mono text-sm">{device.ip_address || '-'}</td>
                          <td className="text-muted-foreground">{device.empresa_nombre || '-'}</td>
                          <td className="text-muted-foreground">{device.site_nombre || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              device.status_name?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : device.status_name?.toLowerCase().includes('baja') || device.status_name?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {device.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="w-32">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(device)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-iot-${device.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(device)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-iot-${device.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(device)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-iot-${device.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, device })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-iot-${device.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, device: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-primary" />
                {viewModal.device?.asset_tag}
              </DialogTitle>
            </DialogHeader>
            {viewModal.device && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Asset Tag</Label>
                    <p className="font-medium">{viewModal.device.asset_tag}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Serial Number</Label>
                    <p className="font-mono text-sm">{viewModal.device.serial_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo</Label>
                    <p>{viewModal.device.tipo_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Marca</Label>
                    <p>{viewModal.device.brand_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <p>{viewModal.device.modelo || '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">IP Address</Label>
                    <p className="font-mono">{viewModal.device.ip_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">MAC Address</Label>
                    <p className="font-mono text-sm">{viewModal.device.mac_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Empresa</Label>
                    <p>{viewModal.device.empresa_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.device.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p>{viewModal.device.ubicacion || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        viewModal.device.status_name?.toLowerCase().includes('activo') 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : viewModal.device.status_name?.toLowerCase().includes('baja') || viewModal.device.status_name?.toLowerCase().includes('inactivo')
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {viewModal.device.status_name || 'Sin estado'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, device: null, isCopy: false })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar Dispositivo' : editModal.device ? 'Editar Dispositivo' : 'Nuevo Dispositivo'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Asset Tag */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Asset Tag *</Label>
                <Input
                  value={formData.asset_tag}
                  onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                  placeholder="IOT-001"
                  data-testid="input-asset-tag"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Serial Number */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Serial Number</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  placeholder="SN123456"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Empresa */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Empresa *</Label>
                <select
                  value={formData.empresa_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, empresa_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-empresa"
                >
                  <option value="">Seleccionar...</option>
                  {options.empresas.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Site */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Site *</Label>
                <select
                  value={formData.site_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-site"
                >
                  <option value="">Seleccionar...</option>
                  {options.sites.map(s => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Tipo */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Tipo</Label>
                <SelectWithCreate
                  value={formData.tipo_id}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_id: v }))}
                  options={options.tipos}
                  placeholder="Seleccionar tipo"
                  onCreate={handleQuickCreateTipo}
                  createLabel="tipo"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Marca */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Marca</Label>
                <SelectWithCreate
                  value={formData.brand_id}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}
                  options={options.brands}
                  placeholder="Seleccionar marca"
                  onCreate={handleQuickCreateBrand}
                  createLabel="marca"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Modelo */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Modelo</Label>
                <Input
                  value={formData.modelo}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="Modelo del dispositivo"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* IP Address */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">IP Address</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="192.168.1.100"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* MAC Address */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">MAC Address</Label>
                <Input
                  value={formData.mac_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Ubicacion */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Ubicacion</Label>
                <Input
                  value={formData.ubicacion}
                  onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
                  placeholder="Rack 1, Piso 2"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              {/* Estado */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Estado</Label>
                <select
                  value={formData.status_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, status_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-status"
                >
                  <option value="">Sin estado</option>
                  {options.estados?.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, device: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, device: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Dispositivo
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el dispositivo <strong>{deleteModal.device?.asset_tag}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, device: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default IoT;
